import {
  Button,
  ButtonsWrapper,
  Form,
  PhoneNumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextInput,
  Typography,
  AlertBlock,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import moment from 'moment';
import { useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import { useTranslation } from 'react-i18next';

import { ChangeAvatarInput } from 'src/pages/employee/sub-pages/employee-details/components/change-avatar-input';
import { useUpdateEmployee } from 'src/hooks/employees/update-employee.hook';
import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { useUpdateEmployeeAvatarById } from 'src/hooks/employees/update-employee-avatar-by-id.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

import { FormItemContainer, WorkingDaysContainer } from './employee-details-edit.styles';
import WorkingDay from './working-day.component';
import {
  checkIfNewRangeIsValid,
  compareWorkingHours,
  modifyWorkingDate,
} from './employee-details-edit.helpers';
import { EmployeeWorkingHoursChangeModal } from './employee-working-hours-change-modal';

const EmployeeDetailsEdit = ({ data, onCancelEdit }: { data: any; onCancelEdit: () => void }) => {
  const { t } = useTranslation(['common', 'employee']);
  const { _id, firstName, lastName, phoneNumber, email, workingDays, ganttId, nickname } = data;
  const [form] = Form.useForm();
  const currentCompanyId = useGetCurrentCompanyId();
  const { gantts } = useGetGantts();
  const [hoursChangeModal, setHoursChangeModal] = useState(false);
  const updateEmployee = useUpdateEmployee({ onSuccess: onCancelEdit });
  const updateEmployeeAvatar = useUpdateEmployeeAvatarById();

  const onFinish = ({ breakTime, ...values }: any) => {
    const fields = form.getFieldsError();
    const someError = fields.some(field => !!field.errors.length);

    if (someError) return;

    const newWorkingDays: any = {};

    Object.entries(values.workingDays).forEach(([key, value]: any) => {
      if (value) {
        if (moment(value[0]).isSame(value[1], 'day')) {
          return (newWorkingDays[key] = {
            to: moment(value[1]).milliseconds(0).toDate(),
            from: moment(value[0]).milliseconds(0).toDate(),
          });
        }
        return (newWorkingDays[key] = modifyWorkingDate(value));
      }
    });

    if (
      !compareWorkingHours(workingDays, { breakTime, ...newWorkingDays }) &&
      !values.workingDays.startFrom
    ) {
      return setHoursChangeModal(true);
    }

    return updateEmployee({
      variables: {
        payload: {
          ...values,
          workingDays: { ...newWorkingDays, startFrom: values.workingDays.startFrom, breakTime },
          _id,
          companyId: currentCompanyId,
        },
      },
    });
  };

  const changeAvatar = (avatar?: RcFile) => {
    updateEmployeeAvatar({ variables: { employeeId: data._id, avatar } });
  };

  return (
    <>
      {hoursChangeModal && (
        <EmployeeWorkingHoursChangeModal
          onSubmit={startFrom => {
            const formValues = form.getFieldsValue();

            formValues.workingDays.startFrom = startFrom;

            onFinish(formValues);
          }}
          onClose={() => setHoursChangeModal(false)}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(_, { breakTime, workingDays }) => {
          const validatedFields = Object.entries(workingDays).map(([key, range]: any) => {
            const errors = [];

            if (breakTime && !checkIfNewRangeIsValid(range, breakTime)) {
              errors.push(t('common:validation.error.wrongWorkingRange'));
            }

            if (range && range[0].format('HH:mm') === range[1].format('HH:mm')) {
              errors.push(t('common:validation.error.workingRangeSame'));
            }

            return { name: ['workingDays', key], errors };
          });

          form.setFields(validatedFields);
        }}
        initialValues={{
          fullName: `${firstName} ${lastName}`,
          phoneNumber,
          email,
          ganttId,
          breakTime: workingDays.breakTime,
          nickname,
        }}
      >
        <FormItemContainer>
          <div>
            <ChangeAvatarInput data={data} onChange={changeAvatar} />
            <Form.Item
              name="fullName"
              label={t('common:labels.fullName')}
              rules={[
                { required: true, message: t('common:validation.error.fullNameRequired') },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput maxLength={50} />
            </Form.Item>
            <Form.Item
              name="nickname"
              label={t('employee:labels.nickname')}
              rules={[{ required: true, message: t('employee:validationError.nicknameRequired') }]}
            >
              <TextInput placeholder="billsander" maxLength={50} />
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              label={t('common:labels.phoneNumber')}
              rules={[
                { required: true, message: t('common:validation.error.phoneNumberRequired') },
              ]}
            >
              <PhoneNumberInput />
            </Form.Item>
            <Form.Item
              name="email"
              label={t('common:labels.email')}
              rules={[{ type: 'email', message: t('common:validation.error.incorrectFormat') }]}
            >
              <TextInput maxLength={50} placeholder="solomonolov@gmail.com" />
            </Form.Item>
            <Form.Item
              name="ganttId"
              label={t('common:labels.gantt')}
              rules={[{ required: true, message: t('employee:validationError.ganttRequired') }]}
            >
              <SelectInput>
                {gantts.map((gantt: any) => (
                  <SelectInput.Option key={gantt._id} value={gantt._id}>
                    {gantt.name}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
          </div>
          <WorkingDaysContainer>
            <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
              {t('employee:labels.workingHours')}
            </Typography>
            {workingDays.startFromString && (
              <AlertBlock
                title={t('employee:labels.note')}
                description={t('employee:labels.noteDescription', {
                  date: workingDays.startFromString,
                })}
              />
            )}
            <div>
              <WorkingDay days={workingDays} condition />
              <WorkingDay days={workingDays} />
            </div>
          </WorkingDaysContainer>
        </FormItemContainer>
        <ButtonsWrapper>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onCancelEdit}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </>
  );
};

export default EmployeeDetailsEdit;
